import { useState } from "react";
import { toast } from "sonner";
import { Camera, CheckCircle2, AlertOctagon, Ban, Car, Wrench, LifeBuoy, ClipboardList, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Panel, Sel, BranchSel, Field, StatusBadge } from "@/components/kit";
import { api, useApi, fmtErr, fileToDataUrl, dt, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TYPES = [["illegal_parking", "Illegal parking", Ban], ["obstruction", "Obstruction", AlertOctagon], ["accident_damage", "Accident / damage", Car],
  ["faulty_equipment", "Faulty equipment", Wrench], ["assistance", "Request assistance", LifeBuoy], ["zone_condition", "Zone condition OK", ClipboardList], ["other", "Other incident", ShieldAlert]];

export default function Attendant() {
  const { branch, branches, setBranch } = useAuth();
  const bid = branch || branches[0]?.id;
  const b = branches.find((x) => x.id === bid);
  const [zone, setZone] = useState("");
  const zid = zone || b?.zones?.[0]?.id;
  const slots = useApi(bid ? "/slots" : null, { branch_id: bid, zone_id: zid });
  const incs = useApi("/incidents", { branch_id: bid });
  const [f, setF] = useState({ type: "illegal_parking", plate: "", slot_code: "", description: "", photo: null });
  const [slot, setSlot] = useState(null);
  const [plate, setPlate] = useState("");
  const confirm = async () => {
    try {
      const { data } = await api.post(`/slots/${slot.id}/confirm`, { plate, occupied: true });
      toast[data.mismatch ? "warning" : "success"](data.mismatch ? "Plate mismatch — exception created" : `Slot ${slot.code} confirmed`);
      setSlot(null);
      setPlate("");
      slots.reload();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  const report = async () => {
    try {
      const { data } = await api.post("/incidents", { ...f, branch_id: bid, zone_id: zid });
      toast.success(`${data.ref} reported`);
      setF({ type: f.type, plate: "", slot_code: "", description: "", photo: null });
      incs.reload();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <div className="mx-auto max-w-2xl fade-up">
      <PageHeader title="Roving Attendant" subtitle={`${b?.code || ""} • tap a slot to confirm, or report an incident`} testid="attendant-header" />
      <div className="mb-4 grid grid-cols-2 gap-2">
        <BranchSel value={bid} onChange={setBranch} all={false} testid="attendant-branch" className="w-full" />
        <Sel value={zid} onChange={setZone} testid="attendant-zone" className="w-full" options={(b?.zones || []).map((z) => ({ value: z.id, label: `${z.name} (${z.floor})` }))} />
      </div>
      <Panel title="Zone slots" className="mb-4">
        <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6">
          {(slots.data || []).map((s) => (
            <button key={s.id} data-testid={`att-slot-${s.code}`} onClick={() => { setSlot(s); setPlate(s.plate || ""); }}
              className={`rounded-md border p-2 text-center font-mono text-[11px] ${s.status === "occupied" ? "border-cyan-500 bg-cyan-500/20" : s.status === "available" ? "border-emerald-500/40 bg-emerald-500/10" : "border-red-500/40 bg-red-500/10"} ${slot?.id === s.id ? "ring-2 ring-amber-500" : ""}`}>
              <div className="font-bold">{s.code}</div><div className="truncate">{s.plate || label(s.status)}</div>
              {s.last_confirmed_at && <CheckCircle2 className="mx-auto mt-0.5 h-3 w-3 text-emerald-500" />}
            </button>
          ))}
        </div>
        {slot && (
          <div className="mt-3 flex gap-2 rounded-md border border-amber-500/50 p-3" data-testid="att-confirm-box">
            <Input data-testid="att-confirm-plate" className="font-mono uppercase" placeholder="Observed plate" value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} />
            <Button data-testid="att-confirm-btn" onClick={confirm}>Confirm {slot.code}</Button>
          </div>
        )}
      </Panel>
      <Panel title="Report" className="mb-4" testid="att-report-panel">
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {TYPES.map(([k, l, I]) => (
            <button key={k} data-testid={`att-type-${k}`} onClick={() => setF({ ...f, type: k })}
              className={`flex flex-col items-center gap-1 rounded-md border p-3 text-xs font-semibold ${f.type === k ? "border-amber-500 bg-amber-500/10 text-amber-500" : ""}`}><I className="h-5 w-5" />{l}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Plate"><Input data-testid="att-plate" className="font-mono uppercase" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} /></Field>
          <Field label="Slot"><Input data-testid="att-slot" value={f.slot_code} onChange={(e) => setF({ ...f, slot_code: e.target.value.toUpperCase() })} /></Field>
        </div>
        <Field label="Description" className="mt-2"><Textarea data-testid="att-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed py-3 text-sm" data-testid="att-photo-label">
          <Camera className="h-4 w-4" /> {f.photo ? "Photo attached ✓" : "Take photo"}
          <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="att-photo" onChange={async (e) => e.target.files[0] && setF({ ...f, photo: await fileToDataUrl(e.target.files[0]) })} />
        </label>
        {f.photo && <img src={f.photo} alt="preview" className="mt-2 max-h-40 rounded" />}
        <Button className="mt-3 w-full bg-amber-500 font-bold text-slate-950 hover:bg-amber-400" data-testid="att-submit" disabled={!f.description} onClick={report}>Submit report</Button>
      </Panel>
      <Panel title="Recent reports">
        {(incs.data || []).slice(0, 10).map((i) => (
          <div key={i.id} className="flex items-center justify-between border-b py-2 text-sm last:border-0">
            <div><div className="font-semibold">{label(i.type)} {i.plate && <span className="plate text-xs">{i.plate}</span>}</div><div className="text-xs text-muted-foreground">{i.description}</div></div>
            <div className="text-right"><StatusBadge status={i.status} /><div className="font-mono text-[10px] text-muted-foreground">{dt(i.created_at)}</div></div>
          </div>
        ))}
      </Panel>
    </div>
  );
}
